import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Wrench, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  ThumbsUp, 
  RefreshCw,
  MessageSquare,
  Plus,
  User,
  MapPin,
  Send,
  History,
  Bell,
  Trash2
} from 'lucide-react';

const statusConfig = {
  todo: { label: 'To Do', color: 'bg-slate-500', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-plp-gold text-plp-blue', icon: AlertCircle },
  done: { label: 'Done', color: 'bg-emerald-500', icon: CheckCircle }
};

const AdminIssues = () => {
  const { token } = useAuth();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [submittingNote, setSubmittingNote] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchIssues = async () => {
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await axios.get(`${API}/issues`, { params });
      setIssues(response.data);
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchIssues(); }, [filter]);

  const handleStatusChange = async (issueId, newStatus, note = '') => {
    try {
      const params = new URLSearchParams({ status: newStatus });
      if (note) params.append('note', note);
      if (newStatus === 'done') params.append('completed_by', 'Killarney Connect Team');
      
      await axios.patch(`${API}/issues/${issueId}/status?${params.toString()}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status updated! Notification sent to reporter.');
      fetchIssues();
      setDialogOpen(false);
      setNoteText('');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAddNote = async () => {
    if (!selectedIssue || !noteText.trim()) return;
    
    setSubmittingNote(true);
    try {
      await axios.post(`${API}/issues/${selectedIssue.id}/notes`, 
        { note: noteText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Progress note added! Notification sent to reporter.');
      setNoteText('');
      // Refresh the issue data
      const res = await axios.get(`${API}/issues/${selectedIssue.id}`);
      setSelectedIssue(res.data);
      fetchIssues();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setSubmittingNote(false);
    }
  };

  const handleDeleteIssue = async (issueId) => {
    if (!window.confirm('Are you sure you want to delete this issue? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/issues/${issueId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Issue deleted successfully');
      fetchIssues();
      setDialogOpen(false);
    } catch (error) {
      toast.error('Failed to delete issue');
    }
  };

  const openIssueDetail = async (issue) => {
    try {
      const res = await axios.get(`${API}/issues/${issue.id}`);
      setSelectedIssue(res.data);
      setDialogOpen(true);
    } catch (error) {
      setSelectedIssue(issue);
      setDialogOpen(true);
    }
  };

  const counts = {
    all: issues.length,
    todo: issues.filter(i => i.status === 'todo').length,
    in_progress: issues.filter(i => i.status === 'in_progress').length,
    done: issues.filter(i => i.status === 'done').length
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Wrench className="w-6 h-6 text-plp-gold" /> Infrastructure Issues
          </h1>
          <p className="text-slate-600">Manage reported issues, add progress notes, and track completion</p>
        </div>
        <Button variant="outline" onClick={fetchIssues} className="border-plp-blue text-plp-blue hover:bg-plp-blue/5">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {Object.entries(statusConfig).map(([key, config]) => (
          <Card 
            key={key} 
            className={`cursor-pointer transition-all ${filter === key ? 'ring-2 ring-plp-blue shadow-md' : 'hover:shadow-md'}`} 
            onClick={() => setFilter(key)}
          >
            <CardContent className="p-4 text-center">
              <config.icon className={`w-6 h-6 mx-auto mb-2 ${key === 'in_progress' ? 'text-plp-gold' : config.color.replace('bg-', 'text-').split(' ')[0]}`} />
              <p className="text-2xl font-heading font-bold text-plp-blue">{counts[key]}</p>
              <p className="text-sm text-slate-500">{config.label}</p>
            </CardContent>
          </Card>
        ))}
        <Card 
          className={`cursor-pointer transition-all ${filter === 'all' ? 'ring-2 ring-plp-blue shadow-md' : 'hover:shadow-md'}`} 
          onClick={() => setFilter('all')}
        >
          <CardContent className="p-4 text-center">
            <Wrench className="w-6 h-6 mx-auto mb-2 text-plp-blue" />
            <p className="text-2xl font-heading font-bold text-plp-blue">{counts.all}</p>
            <p className="text-sm text-slate-500">All Issues</p>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-plp-blue" /></div>
      ) : issues.length === 0 ? (
        <Card className="bg-white"><CardContent className="py-12 text-center"><Wrench className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">No issues found</p></CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {issues.map((issue) => {
            const status = statusConfig[issue.status];
            const hasUpdates = issue.updates && issue.updates.length > 0;
            return (
              <Card key={issue.id} className="bg-white hover:shadow-md transition-shadow" data-testid={`issue-card-${issue.id}`}>
                <CardContent className="p-4">
                  {issue.photo_url && <img src={issue.photo_url} alt="" className="w-full h-32 object-cover rounded-lg mb-3" />}
                  <div className="flex items-start justify-between gap-2">
                    <Badge className={`${status.color} text-white`}><status.icon className="w-3 h-3 mr-1" />{status.label}</Badge>
                    <Badge variant="outline" className="capitalize border-plp-blue/30 text-plp-blue">{issue.issue_type}</Badge>
                  </div>
                  <h3 className="font-heading font-bold mt-2 text-slate-900">{issue.title}</h3>
                  <p className="text-sm text-slate-600 mt-1 line-clamp-2">{issue.description}</p>
                  
                  {hasUpdates && (
                    <div className="mt-2 flex items-center gap-1 text-xs text-plp-blue">
                      <History className="w-3 h-3" />
                      {issue.updates.length} update{issue.updates.length > 1 ? 's' : ''}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between mt-3 pt-3 border-t">
                    <span className="text-xs text-slate-400">{new Date(issue.created_at).toLocaleDateString()}</span>
                    <span className="flex items-center gap-1 text-sm text-slate-500"><ThumbsUp className="w-4 h-4" />{issue.upvotes}</span>
                  </div>
                  
                  <div className="mt-3 flex gap-2">
                    <Select value={issue.status} onValueChange={(v) => handleStatusChange(issue.id, v)}>
                      <SelectTrigger className="flex-1 border-plp-blue/30"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todo">To Do</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="done">Done</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => openIssueDetail(issue)}
                      className="border-plp-blue text-plp-blue hover:bg-plp-blue/5"
                      data-testid={`view-issue-${issue.id}`}
                    >
                      <MessageSquare className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => handleDeleteIssue(issue.id)}
                      className="border-red-300 text-red-500 hover:bg-red-50 hover:border-red-400"
                      data-testid={`delete-issue-${issue.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Issue Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Wrench className="w-5 h-5 text-plp-gold" />
              Issue Details & Progress
            </DialogTitle>
          </DialogHeader>
          
          {selectedIssue && (
            <div className="space-y-6 mt-4">
              {/* Issue Info */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <Badge className={`${statusConfig[selectedIssue.status].color} text-white`}>
                      {statusConfig[selectedIssue.status].label}
                    </Badge>
                    <h3 className="font-heading font-bold text-lg mt-2">{selectedIssue.title}</h3>
                  </div>
                  <Badge variant="outline" className="capitalize">{selectedIssue.issue_type}</Badge>
                </div>
                <p className="text-slate-600">{selectedIssue.description}</p>
                <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-500">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" /> {new Date(selectedIssue.created_at).toLocaleString()}
                  </span>
                  <span className="flex items-center gap-1">
                    <ThumbsUp className="w-4 h-4" /> {selectedIssue.upvotes} upvotes
                  </span>
                  {selectedIssue.user_email && (
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" /> {selectedIssue.user_email}
                    </span>
                  )}
                </div>
                {selectedIssue.photo_url && (
                  <img src={selectedIssue.photo_url} alt="" className="w-full h-48 object-cover rounded-lg mt-3" />
                )}
              </div>

              {/* Progress Updates */}
              <div>
                <h4 className="font-heading font-semibold mb-3 flex items-center gap-2">
                  <History className="w-4 h-4 text-plp-blue" /> Progress Updates
                </h4>
                <ScrollArea className="h-48 border rounded-lg p-4">
                  {(!selectedIssue.updates || selectedIssue.updates.length === 0) ? (
                    <p className="text-center text-slate-400 py-8">No updates yet</p>
                  ) : (
                    <div className="space-y-4">
                      {selectedIssue.updates.map((update, idx) => (
                        <div key={update.created_at || `update-${idx}`} className="p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-slate-900 text-sm">{update.admin_name}</span>
                            <span className="text-xs text-slate-400">{new Date(update.created_at).toLocaleString()}</span>
                          </div>
                          <p className="text-slate-700">{update.note}</p>
                          {update.status_change && (
                            <Badge variant="outline" className="mt-2 text-xs">
                              Status: {update.status_change}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>

              {/* Add Note Form */}
              <div className="p-4 bg-plp-blue/5 rounded-lg border border-plp-blue/20">
                <Label className="font-heading font-semibold flex items-center gap-2 mb-3">
                  <Plus className="w-4 h-4 text-plp-blue" /> Add Progress Note
                </Label>
                <div className="space-y-3">
                  <Textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Enter progress update... (e.g., 'Contractor scheduled for Monday', 'Parts ordered')"
                    rows={3}
                    data-testid="issue-note-input"
                  />
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                      <Bell className="w-3 h-3" /> Reporter will be notified
                    </p>
                    <Button 
                      onClick={handleAddNote}
                      disabled={submittingNote || !noteText.trim()}
                      className="bg-plp-blue hover:bg-plp-blue-light"
                      data-testid="add-note-btn"
                    >
                      {submittingNote ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Add Note
                    </Button>
                  </div>
                </div>
              </div>

              {/* Status Update with Note */}
              <div className="flex gap-3">
                <Select 
                  value={selectedIssue.status} 
                  onValueChange={(v) => {
                    const note = noteText.trim() || `Status updated to ${statusConfig[v].label}`;
                    handleStatusChange(selectedIssue.id, v, note);
                  }}
                >
                  <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todo">To Do</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="done">Done</SelectItem>
                  </SelectContent>
                </Select>
                <Button 
                  variant="outline"
                  onClick={() => handleDeleteIssue(selectedIssue.id)}
                  className="border-red-300 text-red-500 hover:bg-red-50 hover:border-red-400"
                  data-testid="delete-issue-dialog-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminIssues;
